import React from "react";
import Profile from "../models/Profile";
import LiveApiClient, { LiveApiCache } from "../api/LiveApiClient";
import IdentityClient from "../api/IdentityClient";
import { REACT_APP_PUBLIC_API_KEY } from "../constants";
import { Cache } from "../util/Cache.util";
import { isNative, NativeStorage } from "../util/platform";
import { isBiometricLoginEnabled } from "./biometric/featureFlag";
import { CapacitorBiometric } from "./biometric/CapacitorBiometric";

const AuthContext = React.createContext({
	token: undefined,
	currentUser: undefined,
	profile: undefined,
	organizationMember: undefined,
	orgMemberLoading: true,
	setOrganizationMember: () => Promise,
	setOrgMemberLoading: () => {},
	checkAuth: () => Promise,
	login: ({ username, password, pin, otp, mfa }, ignore_errors = false, preventUserSet = undefined) => Promise,
	logout: () => Promise,
	updateCurrentUser: () => Promise,
	updateUserCatalogs: () => Promise,
	requestPin: () => Promise,
	clearStore: () => { },
	isSuperAdmin: () => { },
	completeRegistration: ({ username, password, pin, organization }) => {},
});

export const useAuth = () => {
	const context = React.useContext(AuthContext);
	if (!context) {
		throw new Error("useAuth must be used within a AuthProvider");
	}
	return context;
};

export const AuthProvider = ({ children }) => {
	const storedToken = undefined;
	const [token, setToken] = React.useState(storedToken || undefined);

	const storedCurrentUser = JSON.parse(localStorage.getItem("currentUser"));
	const [currentUser, setCurrentUser] = React.useState(storedCurrentUser || undefined);

	// Grab directly from identity server
	if (currentUser && !currentUser?.email_verified) {
		IdentityClient.post("getToken",).then(tokenResponse => {
			IdentityClient.get('identity/identity', { params: { token: tokenResponse.data.token } })
				.then(({ data }) => {
					if (+data.email_verified) {
						currentUser.email_verified = true;
						setCurrentUser(currentUser);
						localStorage.setItem("currentUser", JSON.stringify(currentUser));
					}
				})
		});
	}

	// Cold-launch biometric path (native only, behind feature flag).
	// Additive: tries biometric first; on any non-success outcome (cancel,
	// unavailable, JWT rejected) control falls through to the existing flow
	// (password login on the Login screen).
	React.useEffect(() => {
		let cancelled = false;
		(async () => {
			if (!isNative() || !isBiometricLoginEnabled()) return;
			// In-app navigation via `window.location = ...` remounts AuthProvider
			// (full page reload). Skip the prompt when a user is already signed in
			// — otherwise Face ID fires on every page transition.
			if (localStorage.getItem("currentUser")) return;
			try {
				if (!(await CapacitorBiometric.isEnrolled())) return;
				const jwt = await CapacitorBiometric.authenticate();
				if (cancelled) return;
				try {
					await checkAuth(jwt);
					return; // success — checkAuth set token + currentUser
				} catch {
					// JWT rejected by /authorize — wipe ONLY the keychain
					// credential, preserving the user's biometric_enrolled
					// intent (per spec decision 7). The next successful
					// password login will re-enroll automatically.
					await CapacitorBiometric.clearStoredCredentialOnly();
					return;
				}
			} catch (err) {
				if (err && err.code === 'user_cancel') {
					// user dismissed — fall through to login screen
					return;
				}
				// hardware unavailable / lockout / unknown — fall through silently
			}
		})();
		return () => { cancelled = true; };
		// Run-once on mount. Auth helpers (checkAuth, isNative, etc.) are stable
		// imports; including them would re-fire and double-invoke biometric auth.
	}, []);

	//profile is never stored into localStorage anywhere
	const storedProfile = new Profile(JSON.parse(localStorage.getItem("profile")));
	const [profile] = React.useState(storedProfile || undefined);

	const [organizationMember, setOrganizationMember] = React.useState(undefined);
	const [orgMemberLoading, setOrgMemberLoading] = React.useState(true);

	// Tracks which username has already had `requires_password_setup` probed
	// during this session, so repeated checkAuth/login calls don't re-fire
	// the network calls below. Reset on logout (see `clearStore`).
	const probedPasswordSetupRef = React.useRef(null);

	// Lazy probe: after currentUser is set, ask the identity server whether
	// this account has no real password set. If it doesn't, we ALSO have to
	// check whether the user's Organization has SSO enabled — SSO accounts
	// genuinely have no password by design and must NOT be flagged for the
	// SetupPasswordModal (that would dead-end them on the SSO redirect path
	// and the original prod bug was Login.js bouncing SSO users to
	// /reset-password for exactly this reason).
	//
	// Only set requires_password_setup=true when has_password is false AND
	// sso_enabled is false. Errors at any step are swallowed — we'd rather
	// miss a modal than show it spuriously.
	const probeRequiresPasswordSetup = (user) => {
		if (!user?.username) return;
		if (probedPasswordSetupRef.current === user.username) return;
		probedPasswordSetupRef.current = user.username;

		IdentityClient.get("password_status/", { params: { username: user.username } })
			.then(async ({ data }) => {
				if (!data || data.has_password) return;

				// has_password is false — could be SSO or OTP-only signup.
				// Look up the user's Organization to check sso_enabled before
				// flipping the flag. Uses the same public-api-key + email-domain
				// lookup that Login.js's fetchOrganizationByEmail uses (proven
				// in prod) — we deliberately do NOT use the auth'd
				// /Organization/<id> endpoint because a freshly-registered
				// user may not yet have read permission on their own org
				// row, which would 401 and (via fail-closed) suppress the
				// modal incorrectly.
				try {
					const domain = (user.username || user.email || "").split("@").at(-1);
					if (domain) {
						const filters = [[
							{ property: "{website}", operator: "CONTAINS", value: domain },
							{ property: "{email_domains}", operator: "IN SET", value: domain },
						]];
						const orgResp = await LiveApiClient.get('/Organization/', {
							headers: { "x-api-key": REACT_APP_PUBLIC_API_KEY },
							params: {
								filters: JSON.stringify(filters),
								paths: JSON.stringify(['sso_enabled']),
								limit: 50,
							},
						});
						// Big brands (e.g. David Yurman) have many Org rows
						// matching the domain — General + every store — and
						// only the General row has sso_enabled. `.find()` it
						// instead of `.pop()`-ing whichever ended up last.
						const ssoOrg = (orgResp?.data || []).find(o => o?.sso_enabled);
						if (ssoOrg) return;
					}
				} catch (orgErr) {
					// Org lookup failed — proceed to flip the flag anyway. The
					// downside (modal shown to a domain-mismatched SSO user)
					// is far milder than the upside (modal works for the vast
					// majority of OTP-only signups whose org domain DOES match).
					console.warn("[auth] org SSO lookup failed; proceeding to set requires_password_setup", orgErr);
				}

				// Read latest from storage to avoid clobbering concurrent
				// updates (e.g. email_verified enrichment above).
				const stored = JSON.parse(localStorage.getItem("currentUser")) || user;
				const enriched = { ...stored, requires_password_setup: true };
				localStorage.setItem("currentUser", JSON.stringify(enriched));
				setCurrentUser(enriched);
			})
			.catch(() => {
				// Network / 404 / other — leave requires_password_setup falsy.
				// Allow a future checkAuth to retry by clearing the cache key.
				probedPasswordSetupRef.current = null;
			});
	};

	const requestPin = async username => {
		const { data } = await IdentityClient.post("otp-login-request", { username });
		return data;
	};

	const checkAuth = async (auth) => {
		return new Promise(async (resolve, reject) => {
			const response = await IdentityClient.post("authorize", { 'auth-token': auth });
			if (!response.data.identity) {
				reject(new Error("Invalid auth token"));
				return;
			}

			LiveApiCache.Set(auth);
			if (isNative()) {
				NativeStorage.setToken(auth).catch(err => console.warn('Failed to persist token to NativeStorage:', err));
			}
			// await LiveApiCache.Set(auth);
			const token = { token: auth, expires_on: +response.data.expires };
			LiveApiCache.Get();
			setToken(token);

			const user = response.data.identity;
			delete user.meta_json;
			delete user.meta;

			localStorage.setItem("currentUser", JSON.stringify(user));
			setCurrentUser(user);
			// Fire-and-forget: enriches currentUser with
			// `requires_password_setup` if the identity server reports
			// `no-password`. Does not block resolution of checkAuth.
			probeRequiresPasswordSetup(user);
			resolve(response?.data);
			return;
		});
	};

	const login = async ({ username, password, pin, otp, mfa }, ignore_errors = false, preventUserSet = undefined) => {
		const response = await IdentityClient.post("authorize", {
			username,
			password,
			pin,
			otp,
			mfa,
		});

		if (response.data.success && response.data.token && response.data.token !== "undefined") {
			setToken(response.data.token);
			const user = response.data.identity;
			delete user.meta_json;
			delete user.meta;

			// currentUser updates are listened for and causes redirect to login; don't always want that
			!preventUserSet && setCurrentUser(user);
			localStorage.setItem("currentUser", JSON.stringify(user));
			// Fire-and-forget password-setup probe. Skip when the caller has
			// asked us not to set currentUser — otherwise the async probe
			// would do exactly that.
			if (!preventUserSet) probeRequiresPasswordSetup(user);
			// Persist JWT to Capacitor Preferences for native token refresh.
			// When biometric enrollment is active, refresh the JWT in the
			// biometric-protected slot instead of plain Preferences.
			if (isNative()) {
				try {
					if (isBiometricLoginEnabled() && await CapacitorBiometric.isEnrolled()) {
						await CapacitorBiometric.enroll(response.data.token);
					} else {
						await NativeStorage.setToken(response.data.token);
					}
				} catch (err) {
					console.warn('Failed to persist token to native storage:', err);
				}
			}
		} else if (!ignore_errors && response.data.errors) {
			logout(true);
		}
		return response?.data;
	};

	const getUserCatalogs = async () => {
		const paths = encodeURIComponent(JSON.stringify({
			id: true,
			label: true,
			title: true,
			uri: true,
			initial_uri: true,
			options: true,
			account: {
				id: true,
				name: true,
				logo: true,
			},
		}));
		const filter = encodeURIComponent(JSON.stringify([{
			"property": "{access.id}",
			"operator": "!=",
			"value": null
		}]));
		const response = await LiveApiClient.get(`Catalog?paths=${paths}&filter=${filter}`);
		return response.data;
	};

	const logout = async (skipRedirect = false) => {
		if (isNative()) {
			await NativeStorage.clearToken();
		} else {
			await IdentityClient.post("deleteToken");
		}
		LiveApiCache.Reset();
		clearStore();
		// setXXX need to be after deleteToken and clearStore or it will appear to not work after reload
		setToken(undefined);
		if (!skipRedirect) {
			window.location.href = "/";
		}
		else {
			// this will cause a redirect from useEffect() in ProtectedLayout
			setCurrentUser(undefined);
		}
	}

	const clearStore = () => {
		// Reset the per-session probe cache so the next login re-checks
		// password status against the identity server.
		probedPasswordSetupRef.current = null;
		localStorage.removeItem('catalog_invitation:data');
		localStorage.removeItem("currentUser");
		localStorage.removeItem("currentCatalog");
		localStorage.removeItem("profile");
		localStorage.removeItem("background");
		localStorage.removeItem("foreground");
		localStorage.removeItem("throttle");
		localStorage.removeItem("organization_member:data");
		localStorage.removeItem("customer");
		Cache.Local().set("has-b2c-catalog", undefined);
		sessionStorage.removeItem("buying_mode");
		// Note: native JWT (`auth_token`) is intentionally NOT cleared here.
		// The logout() flow above is the single source of truth for clearing
		// the token. Per spec decision 4, clearStore is invoked from non-logout
		// paths too (e.g., associate-switch), where the JWT must NOT be wiped.
		// Biometric flags (`biometric_enrolled` / `biometric_prompt_declined`)
		// are likewise preserved here — they are wiped only via the explicit
		// account-switch button on the login screen.
	}

	const updateCurrentUser = (userNewData) => {
		const updatedUser = Object.assign({}, currentUser, userNewData);
		localStorage.setItem("currentUser", JSON.stringify(updatedUser));
		setCurrentUser(updatedUser);
	};
	const updateUserCatalogs = async () => {
		const user = currentUser;
		user.catalogs = await getUserCatalogs();
		setCurrentUser(user);
		localStorage.setItem("currentUser", JSON.stringify(user));
	};
	
	const isSuperAdmin = () => {
		return organizationMember?.profile?.email?.endsWith("@bosslogics.com");
	}
	
	const completeRegistration = async ({ username, password, pin, organization }) => {
		return await IdentityClient.post(
			"complete-registration",
			{
				username, password, pin, organization,
			},
		).then(r => r.data);
	}

	const value = {
		token,
		currentUser,
		profile,
		organizationMember,
		orgMemberLoading,
		setOrganizationMember,
		setOrgMemberLoading,
		checkAuth,
		login,
		logout,
		updateCurrentUser,
		updateUserCatalogs,
		requestPin,
		clearStore,
		isSuperAdmin,
		completeRegistration,
	};

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
